import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Loader2, CreditCard, ExternalLink, AlertCircle, Plus, CheckCircle2 } from "lucide-react";
import { US_STATES } from "@/lib/us-states";
import { Alert, AlertDescription } from "@/components/ui/alert";

const profileSchema = z.object({
  full_name: z.string()
    .trim()
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name must be less than 100 characters")
    .regex(/^[a-zA-Z\s\-'.]+$/, "Name can only contain letters, spaces, hyphens, apostrophes, and periods"),
  phone: z.string()
    .trim()
    .regex(/^\d{10}$/, "Phone number must be exactly 10 digits (no spaces or dashes)")
    .transform(val => val.replace(/\D/g, '')),
  address_line1: z.string()
    .trim()
    .min(3, "Address is required")
    .max(200, "Address is too long"),
  address_line2: z.string()
    .trim()
    .max(200, "Address is too long")
    .optional()
    .or(z.literal("")),
  city: z.string()
    .trim()
    .min(2, "City is required")
    .max(100, "City is too long"),
  state: z.string()
    .trim()
    .length(2, "State must be 2-letter code")
    .regex(/^[A-Z]{2}$/, "State must be uppercase (e.g., NY, CA)"),
  zip: z.string()
    .trim()
    .regex(/^\d{5}$/, "ZIP code must be exactly 5 digits"),
});

type ProfileFormData = z.infer<typeof profileSchema>;

const Settings = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [openingPortal, setOpeningPortal] = useState(false);
  const [addingPayment, setAddingPayment] = useState(false);
  const [linkingCustomer, setLinkingCustomer] = useState(false);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [addressSuggestion, setAddressSuggestion] = useState<any>(null);
  const [paymentInfo, setPaymentInfo] = useState<{
    hasPaymentMethod: boolean;
    card?: { brand: string; last4: string; exp_month: number; exp_year: number };
  } | null>(null);
  const [subscriptionData, setSubscriptionData] = useState<{
    hasSubscription: boolean;
    boxCount: number;
    monthlyCost: number;
  }>({ hasSubscription: false, boxCount: 0, monthlyCost: 0 });

  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      full_name: "",
      phone: "",
      address_line1: "",
      address_line2: "",
      city: "",
      state: "",
      zip: "",
    },
  });

  useEffect(() => {
    loadProfile();
    loadSubscriptionData();
    checkPaymentStatus();
    
    // Handle return from Stripe setup
    const setupStatus = searchParams.get('setup');
    if (setupStatus === 'success') {
      toast({
        title: "Payment method added",
        description: "Your card has been saved successfully.",
      });
      // Re-check payment status after successful setup
      setTimeout(() => checkPaymentStatus(), 1000);
      // Clean up URL
      navigate('/settings', { replace: true });
    } else if (setupStatus === 'cancelled') {
      toast({
        title: "Setup cancelled",
        description: "No payment method was added.",
        variant: "destructive",
      });
      navigate('/settings', { replace: true });
    }
  }, [searchParams]);

  const loadProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/auth");
        return;
      }

      setUserEmail(user.email || "");

      const { data: profile, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      if (error) throw error;

      if (profile) {
        form.reset({
          full_name: profile.full_name || "",
          phone: profile.phone === '0000000000' ? "" : profile.phone || "",
          address_line1: profile.address_line1 || "",
          address_line2: profile.address_line2 || "",
          city: profile.city || "",
          state: profile.state || "",
          zip: profile.zip || "",
        });
      }
    } catch (error) {
      console.error("Error loading profile:", error);
      toast({
        title: "Error",
        description: "Failed to load profile information",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('link-stripe-customer');
      if (error) {
        console.error("Error checking payment status:", error);
        return;
      }
      if (data?.linked && data?.hasPaymentMethod) {
        setPaymentInfo({
          hasPaymentMethod: true,
          card: data.card,
        });
      } else {
        setPaymentInfo({ hasPaymentMethod: false });
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
    }
  };

  const handleAddPaymentMethod = async () => {
    setAddingPayment(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-setup-session');
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error creating setup session:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to open payment setup",
        variant: "destructive",
      });
    } finally {
      setAddingPayment(false);
    }
  };

  const loadSubscriptionData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Primary check: Query active subscription items from subscription_items table
      const { data: subscriptionItems, error } = await supabase
        .from('subscription_items')
        .select('id, monthly_rate')
        .eq('user_id', user.id)
        .eq('status', 'active');

      if (error) throw error;

      const itemCount = subscriptionItems?.length || 0;

      if (itemCount > 0) {
        // Normal flow: user has subscription_items records
        const monthlyCost = subscriptionItems.reduce(
          (sum, item) => sum + (Number(item.monthly_rate) || 9.99),
          0
        );
        setSubscriptionData({ hasSubscription: true, boxCount: itemCount, monthlyCost });
        return;
      }

      // Fallback for migrated users: check profile for stripe_subscription_id
      const { data: profile } = await supabase
        .from('profiles')
        .select('stripe_subscription_id, box_slots_purchased')
        .eq('id', user.id)
        .single();

      if (profile?.stripe_subscription_id) {
        const { data: storedBoxes } = await supabase
          .from('boxes')
          .select('id')
          .eq('user_id', user.id)
          .eq('location_status', 'stored');

        const count = storedBoxes?.length || profile.box_slots_purchased || 0;
        setSubscriptionData({
          hasSubscription: true,
          boxCount: count,
          monthlyCost: count * 9.99,
        });
        return;
      }

      // No subscription found
      setSubscriptionData({ hasSubscription: false, boxCount: 0, monthlyCost: 0 });
    } catch (error) {
      console.error("Error loading subscription data:", error);
    }
  };

  const handleOpenCustomerPortal = async () => {
    setOpeningPortal(true);
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to open billing portal",
        variant: "destructive",
      });
    } finally {
      setOpeningPortal(false);
    }
  };

  const onSubmit = async (data: ProfileFormData, bypassVerification = false) => {
    setSaving(true);
    setAddressError(null);
    setAddressSuggestion(null);
    
    try {
      let verifiedAddress = null;
      
      // Skip verification if user chose to bypass
      if (!bypassVerification) {
        // First, verify the address with EasyPost
        setVerifying(true);
        const { data: verificationResult, error: verifyError } = await supabase.functions.invoke('verify-address', {
          body: {
            street1: data.address_line1,
            street2: data.address_line2 || undefined,
            city: data.city,
            state: data.state,
            zip: data.zip,
          }
        });

        setVerifying(false);

        if (verifyError || !verificationResult?.verified) {
          setAddressError(verificationResult?.error || 'Address could not be verified');
          if (verificationResult?.suggestion) {
            setAddressSuggestion(verificationResult.suggestion);
          }
          toast({
            title: "Address Verification Failed",
            description: verificationResult?.error || "Please check your address and try again. You can also choose to save anyway.",
            variant: "destructive",
          });
          setSaving(false);
          return;
        }

        // If address is verified, use the standardized address
        verifiedAddress = verificationResult.address;
      }
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      // Use verified address if available, otherwise use user input
      const addressToSave = verifiedAddress || {
        street1: data.address_line1,
        street2: data.address_line2,
        city: data.city,
        state: data.state,
        zip: data.zip,
      };

      const { error } = await supabase
        .from("profiles")
        .update({
          full_name: data.full_name,
          phone: data.phone,
          address_line1: addressToSave.street1,
          address_line2: addressToSave.street2 || null,
          city: addressToSave.city,
          state: addressToSave.state,
          zip: addressToSave.zip,
          onboarding_completed: true,
          updated_at: new Date().toISOString(),
        })
        .eq("id", user.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: bypassVerification 
          ? "Profile updated (address verification bypassed)" 
          : "Profile updated with verified address",
      });

      // Reload profile to show the standardized address
      await loadProfile();
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        title: "Error",
        description: "Failed to update profile",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
      setVerifying(false);
    }
  };

  const useAddressSuggestion = () => {
    if (!addressSuggestion) return;
    
    form.setValue('address_line1', addressSuggestion.street1);
    form.setValue('address_line2', addressSuggestion.street2 || '');
    form.setValue('city', addressSuggestion.city);
    form.setValue('state', addressSuggestion.state);
    form.setValue('zip', addressSuggestion.zip);
    
    setAddressError(null);
    setAddressSuggestion(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-2xl mx-auto space-y-6">
        <Button variant="ghost" onClick={() => navigate("/dashboard")} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Button>

        <div>
          <h1 className="text-3xl font-bold">Settings</h1>
          <p className="text-muted-foreground mt-1">Manage your account and preferences</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Shipping Information</CardTitle>
            <CardDescription>Required for pickup and delivery services</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit((data) => onSubmit(data, false))} className="space-y-4">
                <FormField
                  control={form.control}
                  name="full_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full Name</FormLabel>
                      <FormControl>
                        <Input placeholder="John Doe" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div>
                  <Label>Email</Label>
                  <Input value={userEmail} disabled className="bg-muted" />
                  <p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p>
                </div>

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone Number (10 digits) *</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="5551234567" 
                          {...field}
                          maxLength={10}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">Required for shipping labels and pickup coordination</p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="address_line1"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address Line 1</FormLabel>
                      <FormControl>
                        <Input placeholder="123 Main St" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="address_line2"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address Line 2 (Optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="Apt 4B" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>City</FormLabel>
                        <FormControl>
                          <Input placeholder="New York" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="state"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>State</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select state" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="max-h-[200px] bg-background z-50">
                            {US_STATES.map((state) => (
                              <SelectItem key={state.value} value={state.value}>
                                {state.label} ({state.value})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="zip"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ZIP Code</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="12345" 
                          {...field}
                          maxLength={5}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {addressError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      {addressError}
                      {addressSuggestion && (
                        <div className="mt-2">
                          <p className="font-semibold mb-1">Did you mean:</p>
                          <p className="text-sm">
                            {addressSuggestion.street1}
                            {addressSuggestion.street2 && `, ${addressSuggestion.street2}`}
                            <br />
                            {addressSuggestion.city}, {addressSuggestion.state} {addressSuggestion.zip}
                          </p>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="mt-2"
                            onClick={useAddressSuggestion}
                          >
                            Use This Address
                          </Button>
                        </div>
                      )}
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-sm mb-2">If you believe your address is correct:</p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const formData = form.getValues();
                            onSubmit(formData, true);
                          }}
                          disabled={saving || verifying}
                        >
                          Save Anyway (Bypass Verification)
                        </Button>
                      </div>
                    </AlertDescription>
                  </Alert>
                )}

                <Button type="submit" className="w-full" disabled={saving || verifying}>
                  {verifying ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying Address...
                    </>
                  ) : saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Billing & Subscription
            </CardTitle>
            <CardDescription>Manage your subscription and billing information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Payment Method Section */}
            <div className="p-4 bg-muted rounded-lg space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Payment Method</span>
                {paymentInfo?.hasPaymentMethod && (
                  <span className="flex items-center gap-1 text-xs text-green-600">
                    <CheckCircle2 className="h-3 w-3" />
                    Active
                  </span>
                )}
              </div>
              
              {paymentInfo?.hasPaymentMethod && paymentInfo.card ? (
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-background rounded">
                    <CreditCard className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-medium capitalize">
                      {paymentInfo.card.brand} •••• {paymentInfo.card.last4}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Expires {paymentInfo.card.exp_month}/{paymentInfo.card.exp_year}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No payment method on file
                </p>
              )}
              
              <Button
                onClick={handleAddPaymentMethod}
                disabled={addingPayment}
                variant={paymentInfo?.hasPaymentMethod ? "outline" : "default"}
                size="sm"
                className="w-full"
              >
                {addingPayment ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Opening...
                  </>
                ) : (
                  <>
                    <Plus className="mr-2 h-4 w-4" />
                    {paymentInfo?.hasPaymentMethod ? "Update Payment Method" : "Add Payment Method"}
                  </>
                )}
              </Button>
            </div>

            {/* Subscription Section */}
            {subscriptionData.hasSubscription ? (
              <div className="space-y-4">
                {/* Past Due Warning */}
                {!paymentInfo?.hasPaymentMethod && (
                  <Alert className="border-amber-500/50 bg-amber-50 dark:bg-amber-950/20">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-800 dark:text-amber-200">
                      Your subscription is past due. Please update your payment method to avoid service interruption.
                    </AlertDescription>
                  </Alert>
                )}
                <div className="p-4 bg-muted rounded-lg space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">
                      {paymentInfo?.hasPaymentMethod ? "Active Subscriptions" : "Past Due Subscriptions"}
                    </span>
                    <span className="font-semibold">{subscriptionData.boxCount}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Monthly Cost</span>
                    <span className="text-lg font-bold text-primary">
                      ${subscriptionData.monthlyCost.toFixed(2)}
                    </span>
                  </div>
                </div>

                <Button
                  onClick={handleOpenCustomerPortal}
                  disabled={openingPortal}
                  className="w-full"
                >
                  {openingPortal ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Opening...
                    </>
                  ) : (
                    <>
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Manage Billing & View Invoices
                    </>
                  )}
                </Button>

                <p className="text-xs text-muted-foreground text-center">
                  View your billing history, update payment methods, and manage your subscription through Stripe's secure portal.
                </p>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-muted-foreground mb-3">
                  No active subscription yet. Your billing will start when your boxes arrive at our facility.
                </p>
                <Button onClick={() => navigate('/schedule-pickup')} variant="outline" size="sm">
                  Schedule Pickup
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Security</CardTitle>
            <CardDescription>Manage your account security</CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="outline"
              onClick={async () => {
                const { error } = await supabase.auth.resetPasswordForEmail(userEmail, {
                  redirectTo: `${window.location.origin}/auth`,
                });
                if (error) {
                  toast({
                    title: "Error",
                    description: error.message,
                    variant: "destructive",
                  });
                } else {
                  toast({
                    title: "Password reset email sent",
                    description: "Check your email for the reset link",
                  });
                }
              }}
            >
              Change Password
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Settings;
